import React, { useState } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import { useDrag, useDrop } from 'react-dnd'

import { useMutation, useQuery } from 'components/graphql'
import ContentBlock from 'components/ContentBlock'
import { Form, Text, Select } from 'components/forms'
import { ServiceModal, ConfirmModal } from 'components/Modal'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { getContentLanguageLabel } from 'components/ContentTranslations'
import Close from 'components/shared/icons/Close'

import availableLocales from './availableLocales'
import { listContentTranslationLanguages, createLanguageMutation, updateLanguageOrderMutation, deleteLanguageMutation } from './gql'
import { StyledDragable, StyledModalContainer } from './styled'

const ContentTranslations = () => {
	const [showModal, setShowModal] = useState(false)
	const [newLanguage, setNewLanguage] = useState('')
	const languagesRes = useQuery(listContentTranslationLanguages)
	const [t, i18n] = useTranslation()
	const [updateLanguageOrder] = useMutation(updateLanguageOrderMutation)
	const [createLanguage] = useMutation(createLanguageMutation)
	const [deleteLanguage] = useMutation(deleteLanguageMutation)
	const languageData = [...languagesRes.data?.listContentTranslationLanguages?.items || []]
	const languageLocales = languageData?.map(({ locale }) => locale) || []

	const countryList = availableLocales.map(locale => ({
		value: locale,
		label: getContentLanguageLabel(locale, i18n.store.data?.[i18n.language]?.locale)
	}))

	const handleCreate = () => {
		createLanguage({
			variables: { locale: newLanguage.value },
			refetchQueries: [{ query: listContentTranslationLanguages }]
		})
	}

	const handleDelete = id => {
		ConfirmModal({
			text: t('common.strings.are_you_sure')
		}).then(confirmed => deleteLanguage({
			variables: { id },
			refetchQueries: [{ query: listContentTranslationLanguages }]
		}))
	}

	const handleDrop = (dropIndex, dropedItem) => {
		let newOrder = [...languageData]

		// changes droped item position to drop index in array
		const [element] = newOrder.splice(dropedItem.index, 1)
		const newIndex = dropIndex > dropedItem.index ? dropIndex - 1 : dropIndex
		newOrder.splice(newIndex, 0, element)

		newOrder = newOrder.map((item, i) => ({
			id: item.id,
			locale: item.locale,
			order: i + 1
		}))

		updateLanguageOrder({
			variables: { records: newOrder.map(({ locale, ...item }) => item) },
			optimisticResponse: {
				__typename: 'Mutation',
				cashAssist: {
					__typename: 'CA_Mutation',
					updateContentTranslationLanguageOrder: newOrder.map(item => ({
						...item,
						__typename: 'ContentTranslationLanguage'
					}))
				}
			}
		})
	}

	return(
		<ContentBlock isLoading={languagesRes.loading}>
			<Form
				smartSubmit
				initialValues={{
					languages: languageData
						.sort((a, b) => a.order - b.order)
						.map(({ id, locale }) =>
							countryList.find(item => item.value === locale)?.label
						)
				}}
			>
				<Row>
					<Col xs>
						<h5>{t('options_section.fields.translationLanguages')}</h5>
						{languageData.map((item, i) => (
							<div key={item.id}>
								<Dragable index={i}>
									<Text readOnly field={`languages[${i}]`}/>
									<Close onClick={() => handleDelete(item.id)}/>
								</Dragable>
								<DropTarget onDrop={(dropedItem) => handleDrop(i + 1, dropedItem)}/>
							</div>
						))}
					</Col>
				</Row>
				<Divider height={20}/>
				<Col type='row'>
					<Button type='button' status='primary' onClick={() => setShowModal(true)} text={t('common.buttons.create')}/>
				</Col>
			</Form>
			{showModal &&
				<ServiceModal
					onClose={() => setShowModal(false)}
					label={t('common.buttons.confirm')}
					btnText={t('common.buttons.confirm')}
					btnCancelText={t('common.buttons.cancel')}
					text={t('options_section.strings.newTranslationLanguageConfirmation')}
					width={340}
					onOk={() => {
						handleCreate()
						setShowModal(false)
					}}
				>
					<StyledModalContainer>
						<Select
							noForm
							label={t('options_section.fields.newLanguage')}
							options={countryList.filter(({ value }) => !languageLocales.includes(value))}
							onChange={opt => setNewLanguage(opt)}
							isSearchable
						/>
						<Divider height={500}/>
					</StyledModalContainer>
				</ServiceModal>
			}
		</ContentBlock>
	)
}

const Dragable = ({ children, index }) => {
	const [, drag] = useDrag({
		item: { type: 'row', index },
		canDrag: true
	})
	return(
		<StyledDragable ref={drag}>
			{children}
		</StyledDragable>
	)
}

const DropTarget = ({ onDrop }) => {
	const [, dropTargetRef] = useDrop({
		accept: 'row',
		drop: onDrop
	})
	return (
		<div style={{ display: 'flex' }} ref={dropTargetRef}>
			<Divider height={20} color='#3e4c57' visible/>
		</div>
	)
}

export default ContentTranslations
